import { z } from 'zod'

const phoneRegex = /^1[3-9]\d{9}$/
const idCardRegex = /(^\d{15}$)|(^\d{17}[\dXx]$)/

const optionalEmailSchema = z
	.string()
	.email({ message: '请输入有效的邮箱地址' })
	.optional()
	.or(z.literal(''))

const optionalPhoneSchema = z
	.string()
	.regex(phoneRegex, { message: '请输入有效的手机号' })
	.optional()
	.or(z.literal(''))

const optionalIdCardSchema = z
	.string()
	.regex(idCardRegex, { message: '请输入有效的身份证号' })
	.optional()
	.or(z.literal(''))

// ============================================
// 用户表单验证 Schema
// ============================================
export const createUserSchema = z.object({
	username: z
		.string()
		.min(2, { message: '用户名至少 2 个字符' })
		.max(20, { message: '用户名最多 20 个字符' }),

	email: optionalEmailSchema,

	phone: z
		.string()
		.min(1, { message: '请输入手机号' })
		.regex(phoneRegex, { message: '请输入有效的手机号' }),

	role: z.enum(['admin', 'manager', 'operator']),

	status: z.enum(['active', 'inactive', 'suspended']),

	password: z
		.string()
		.min(8, { message: '密码至少 8 个字符' })
		.max(32, { message: '密码最多 32 个字符' })
		.regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, {
			message: '密码必须包含大小写字母和数字'
		}),

	confirmPassword: z
		.string()
		.min(1, { message: '请确认密码' }),
}).refine((data) => data.password === data.confirmPassword, {
	message: '两次输入的密码不一致',
	path: ['confirmPassword'],
})

export type CreateUserData = z.infer<typeof createUserSchema>

// ============================================
// 用户编辑表单 Schema（可选字段）
// ============================================
export const updateUserSchema = z.object({
	username: z
		.string()
		.min(2, { message: '用户名至少 2 个字符' })
		.max(20, { message: '用户名最多 20 个字符' }),

	nickname: z
		.string()
		.optional()
		.or(z.literal('')),

	email: optionalEmailSchema,

	phone: z
		.string()
		.min(1, { message: '请输入手机号' })
		.regex(phoneRegex, { message: '请输入有效的手机号' }),

	avatar: z.string().optional().or(z.literal('')),

	department_id: z.number().nullable().optional(),

	status: z.number().optional(),

	is_super_admin: z.boolean().optional(),

	password: z
		.string()
		.min(6, { message: '密码至少6个字符' })
		.max(32, { message: '密码最多32个字符' })
		.optional()
		.or(z.literal('')),

	wechat: z.string().optional().or(z.literal('')),
	gender: z.number().optional(),
	card_no: optionalIdCardSchema,
	card_front: z.string().optional().or(z.literal('')),
	card_back: z.string().optional().or(z.literal('')),
	emergency_contact: z.string().optional().or(z.literal('')),
	emergency_mobile: optionalPhoneSchema,
	address: z.string().optional().or(z.literal('')),
	remark: z.string().optional().or(z.literal('')),
	self_discipline: z.string().optional().or(z.literal('')),
})

export type UpdateUserData = z.infer<typeof updateUserSchema>

// ============================================
// 登录表单 Schema
// ============================================
export const loginSchema = z.object({
	username: z
		.string()
		.min(1, { message: '请输入用户名' }),

	password: z
		.string()
		.min(1, { message: '请输入密码' }),

	remember: z.boolean().optional(),
})

export type LoginData = z.infer<typeof loginSchema>
